import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import type { Session } from '@supabase/supabase-js'

interface User {
  id: string
  email: string
  username: string
  name: string
  isAdmin?: boolean
  profilePicture?: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (user: User) => void
  logout: () => void
  updateUser: (updates: Partial<User>) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Map a Supabase session + profile row into our app's User shape
const mapProfile = (profile: Record<string, unknown>): User => ({
  id: profile.id as string,
  email: profile.email as string,
  username: profile.username as string,
  name: profile.name as string,
  isAdmin: profile.is_admin as boolean | undefined,
  profilePicture: profile.profile_picture as string | undefined,
})

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Fetch profile from Supabase for a given auth user id
  const fetchProfile = async (userId: string): Promise<User | null> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (error || !data) return null
    return mapProfile(data)
  }

  // On mount: check for existing Supabase session
  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        const profile = await fetchProfile(session.user.id)
        if (profile) setUser(profile)
      }
      setLoading(false)
    }
    init()

    // Listen for auth state changes (sign in, sign out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event: string, session: Session | null) => {
        if (session?.user) {
          const profile = await fetchProfile(session.user.id)
          if (profile) setUser(profile)
        } else {
          setUser(null)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  // login is called after successful auth — just set local state
  // (Supabase session is already established at this point)
  const login = (userData: User) => {
    setUser(userData)
  }

  const logout = async () => {
    await supabase.auth.signOut()
    setUser(null)
  }

  const updateUser = async (updates: Partial<User>) => {
    if (!user) return

    if (updates.username && (!updates.username.trim() || updates.username === '---')) {
      console.error('Invalid username provided')
      return
    }

    // Map our app fields to Supabase column names
    const dbUpdates: Record<string, unknown> = {}
    if (updates.username !== undefined) dbUpdates.username = updates.username
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.email !== undefined) dbUpdates.email = updates.email
    if (updates.profilePicture !== undefined) dbUpdates.profile_picture = updates.profilePicture
    dbUpdates.updated_at = new Date().toISOString()

    const { error } = await supabase
      .from('profiles')
      .update(dbUpdates)
      .eq('id', user.id)

    if (error) {
      console.error('Failed to update profile:', error.message)
      return
    }

    // Update local state
    const updatedUser = { ...user, ...updates }
    setUser(updatedUser)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
